import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import {
  getGoogleFontWeights,
  getUrlForGoogleFont,
} from "containers/admin/clients/client/theme-page/preview/helpers/formatters-on-post-data/formatters-on-post-data";

import { GoogleFontOption } from "containers/admin/clients/client/theme-page/preview/preview.types";
import { GoogleFont } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/google-fonts.types";

export const useGoogleFonts = () => {
  const { fonts, setFonts, typography, setTypography, googleFontsList } = useThemePreviewContext();
  const { getValues, setValue } = useFormContext();

  const [googleFontsSubmitError, setGoogleFontsSubmitError] = useState("");

  // helper to load chosen Google font
  const loadGoogleFont = async (family: string): Promise<void> => {
    if (!family) return;

    // Check if font is already loaded
    const existingLink = document.querySelector(`link[data-google-font="${family}"]`);
    if (existingLink) {
      return;
    }

    const currentGoogleFont = googleFontsList.find((font: GoogleFont) => font.family === family)!;
    const weights = getGoogleFontWeights(currentGoogleFont?.variants);
    const uniqueWeights = Array.from(new Set(weights));

    const fontUrl = getUrlForGoogleFont(family, uniqueWeights as string[]);

    // Don't proceed if URL is empty
    if (!fontUrl || fontUrl === "") {
      return;
    }

    const link = document.createElement("link");
    link.href = fontUrl;
    link.rel = "stylesheet";
    link.setAttribute("data-google-font", family);

    return new Promise((resolve, reject) => {
      link.onload = () => {
        resolve();
      };
      link.onerror = () => reject(new Error("Failed to load Google Font"));
      document.head.appendChild(link);
    });
  };

  // helper to remove Google font stylesheet
  const removeGoogleFont = (family: string) => {
    if (!family) return;

    // Find and remove the stylesheet using data attribute
    const stylesheet = document.querySelector(`link[data-google-font="${family}"]`);
    if (stylesheet) {
      stylesheet.parentNode?.removeChild(stylesheet);
    }
  };

  // helper to set Google fonts to context
  const setUpdatedGoggleFonts = (currentGoogleFontsList: GoogleFontOption[]) => {
    setFonts({
      ...fonts,
      adobeFontsProjectId: fonts?.adobeFontsProjectId || "",
      googleFonts: currentGoogleFontsList,
    });
  };

  // reset typography sections using removed font
  const updateTypographyFont = (removedFont: string) => {
    if (!typography) return;

    const updatedTypography = { ...typography };
    // check all typography sections
    Object.keys(typography).forEach((sectionName) => {
      const section = typography[sectionName];

      // reset font family if it matches the removed font
      if (section.fontFamily.value === removedFont) {
        updatedTypography[sectionName] = {
          ...section,
          fontFamily: { label: "Select...", value: "" },
        };

        setValue(`typography.${sectionName}.fontFamily`, { label: "Select...", value: "" });
      }
    });

    // update typography context
    setTypography(updatedTypography);
  };

  // set updated amount of Google fonts after removing
  const updateGoogleFontsList = (removedFontValue?: string) => {
    const currentGoogleFontsList = getValues("fonts.googleFonts");
    setUpdatedGoggleFonts(currentGoogleFontsList);

    // reset typography sections if a font was removed
    if (removedFontValue) {
      updateTypographyFont(removedFontValue);
    }
  };

  // reset Google fonts list if there is only one field and it was cleared
  const handleGoogleFontClear = (removedFontValue?: string) => {
    const currentGoogleFontsList = [
      {
        value: null,
        label: null,
        appliedToPreview: false,
        fontFullUrl: "",
      },
    ];

    setValue("fonts.googleFonts", currentGoogleFontsList, { shouldDirty: true });
    setUpdatedGoggleFonts(currentGoogleFontsList);

    // reset typography sections if a font was removed
    if (removedFontValue) {
      updateTypographyFont(removedFontValue);
    }
  };

  // set new Google font that should not be applied to Preview area by default
  const handleGoogleFontChange = (index: number, newFont: any, oldFont?: string) => {
    const currentGoogleFontsList = getValues("fonts.googleFonts");
    currentGoogleFontsList[index] = { ...newFont, appliedToPreview: false };

    setUpdatedGoggleFonts(currentGoogleFontsList);

    if (oldFont) {
      updateTypographyFont(oldFont);
      removeGoogleFont(oldFont);
    }
  };

  // apply chosen Google font to Preview area
  const handleGoogleFontSubmit = async (e: React.MouseEvent, newFont: any, fontIndex: number) => {
    e.preventDefault();

    if (!newFont.label) return;

    try {
      // Load the font first
      await loadGoogleFont(newFont.value);

      // Update the Google fonts list in the form
      const currentGoogleFontsList = getValues("fonts.googleFonts");
      currentGoogleFontsList[fontIndex] = { ...newFont, appliedToPreview: true };

      setUpdatedGoggleFonts(currentGoogleFontsList);
      setGoogleFontsSubmitError("");
    } catch (err) {
      setGoogleFontsSubmitError((err as Error).message);
    }
  };

  return {
    googleFontsSubmitError,
    updateGoogleFontsList,
    loadGoogleFont,
    removeGoogleFont,
    // handle events
    handleGoogleFontClear,
    handleGoogleFontChange,
    handleGoogleFontSubmit,
  };
};
